/**
 * Admin → SpamGuard review queue — /admin/spam-review. Platform-only.
 *
 * Lists campaigns SpamGuard held at launch (verdict='review') or blocked
 * (verdict='block') awaiting an operator decision. The operator either
 * approves (clears the hold — they then re-launch from the campaign page)
 * or blocks (records the rejection). The numeric score is shown here to
 * the platform operator only; it is never surfaced to the tenant.
 */

import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, Skeleton } from '@/components/ui';
import {
  useDecideSpamReview,
  useSpamReview,
  type SpamVerdict,
  type SpamVerdictRow,
} from '@/hooks/useSpamGuard';
import { ApiError } from '@/lib/api';

function verdictBadge(v: SpamVerdict): { variant: 'success' | 'danger' | 'warn' | 'info' | 'gray'; label: string } {
  switch (v) {
    case 'block':
      return { variant: 'danger', label: 'block' };
    case 'review':
      return { variant: 'warn', label: 'review' };
    case 'unscored':
      return { variant: 'gray', label: 'unscored' };
    default:
      return { variant: 'info', label: v };
  }
}

interface RowProps {
  v: SpamVerdictRow;
  setError: (msg: string | null) => void;
}

function ReviewRow({ v, setError }: RowProps): JSX.Element {
  const { t } = useTranslation('default');
  const decide = useDecideSpamReview();
  const badge = verdictBadge(v.verdict);

  const act = async (decision: 'approved' | 'blocked') => {
    setError(null);
    try {
      await decide.mutateAsync({ id: v.id, decision });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('sg_review_err_generic'));
    }
  };

  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2 align-top">
      <td className="px-5 py-3 text-text-2 whitespace-nowrap">{v.tenant_name}</td>
      <td className="px-5 py-3 text-text whitespace-nowrap">{v.campaign_name}</td>
      <td className="px-5 py-3 text-text-2 uppercase num">{v.channel}</td>
      <td className="px-5 py-3">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        {v.score !== null && (
          <span className="text-[11px] text-text-4 ml-2 num">{v.score}/100</span>
        )}
      </td>
      <td className="px-5 py-3">
        {v.reasons.length === 0 ? (
          <span className="text-text-4">—</span>
        ) : (
          <ul className="space-y-1 max-w-[360px]">
            {v.reasons.map((r, i) => (
              <li key={`${r.code}-${i}`} className="text-[12px] leading-snug">
                <span className="font-medium text-text-2">{r.code}</span>
                {r.detail && <span className="text-text-3"> — {r.detail}</span>}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" onClick={() => void act('approved')} disabled={decide.isPending}>
            {t('sg_review_approve')}
          </Button>
          <Button
            size="sm"
            className="bg-danger text-white hover:bg-danger/90"
            onClick={() => void act('blocked')}
            disabled={decide.isPending}
          >
            {t('sg_review_block')}
          </Button>
        </div>
      </td>
    </tr>
  );
}

/** One row in the "Blocked recently" section: read-only detail plus the
 *  override-approve escape hatch for false positives. After overriding,
 *  the operator relaunches the campaign — unchanged content then passes
 *  the screener. */
function BlockedRow({ v, setError }: RowProps): JSX.Element {
  const { t } = useTranslation('default');
  const decide = useDecideSpamReview();

  const override = async () => {
    setError(null);
    try {
      await decide.mutateAsync({ id: v.id, decision: 'approved' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('sg_review_err_generic'));
    }
  };

  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2 align-top">
      <td className="px-5 py-3 text-text-2 whitespace-nowrap">{v.tenant_name}</td>
      <td className="px-5 py-3 text-text whitespace-nowrap">{v.campaign_name}</td>
      <td className="px-5 py-3 text-text-2 uppercase num">{v.channel}</td>
      <td className="px-5 py-3">
        <Badge variant="danger">block</Badge>
        {v.score !== null && (
          <span className="text-[11px] text-text-4 ml-2 num">{v.score}/100</span>
        )}
      </td>
      <td className="px-5 py-3">
        {v.reasons.length === 0 ? (
          <span className="text-text-4">—</span>
        ) : (
          <ul className="space-y-1 max-w-[360px]">
            {v.reasons.map((r, i) => (
              <li key={`${r.code}-${i}`} className="text-[12px] leading-snug">
                <span className="font-medium text-text-2">{r.code}</span>
                {r.detail && <span className="text-text-3"> — {r.detail}</span>}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-2">
          {v.decision === 'approved' ? (
            <Badge variant="success">{t('sg_review_overridden')}</Badge>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void override()}
              disabled={decide.isPending}
            >
              {t('sg_review_override')}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminSpamReviewPage(): JSX.Element {
  const { t } = useTranslation('default');
  const list = useSpamReview();
  const [actionError, setActionError] = useState<string | null>(null);

  const items = list.data?.items ?? [];
  const blocked = list.data?.blocked ?? [];

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
          {t('sg_review_title')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1">
          {items.length === 0
            ? t('sg_review_subtitle_empty')
            : t('sg_review_subtitle', { count: items.length })}
        </p>
      </header>

      <Card padding="none">
        {actionError && (
          <div className="px-5 py-2 bg-danger-bg border-b border-danger-bd text-[13px] text-danger">
            {actionError}
          </div>
        )}

        {list.isLoading ? (
          <div className="p-5 space-y-2.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <div className="p-8 text-center">
            <p className="text-[13px] text-danger">{t('sg_review_load_error')}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void list.refetch()}>
              {t('sg_retry')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('sg_review_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('sg_review_empty_help')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_tenant')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_campaign')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_channel')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_verdict')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_reasons')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('sg_review_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <ReviewRow key={v.id} v={v} setError={setActionError} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Blocked recently — hard blocks need no decision, but showing them
          gives the operator visibility and a false-positive escape hatch. */}
      <header className="mt-10 mb-4">
        <h2 className="display text-[18px] font-semibold tracking-tight text-text m-0">
          {t('sg_blocked_title')}
        </h2>
        <p className="text-[13px] text-text-3 mt-1">{t('sg_blocked_subtitle')}</p>
      </header>
      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2.5">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : blocked.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-[13px] text-text-3">{t('sg_blocked_empty')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_tenant')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_campaign')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_channel')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_verdict')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_review_col_reasons')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('sg_review_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {blocked.map((v) => (
                  <BlockedRow key={v.id} v={v} setError={setActionError} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
